import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import AccountPickerContext from 'contexts/AccountPickerContext';
import useHasAccess from 'hooks/useHasAccess';
import STORE from 'store';

// Loads the report doc by id, gated by access. Shared by the interactive
// ReportShow (which reads :reportId from the route) and the headless
// PrintReportShow (which passes reportId explicitly, since it isn't mounted under
// a matching <Route> so useParams would be empty).
const useReport = ({
  reportId: reportIdOverride,
}: { reportId?: string } = {}) => {
  const { reportId: paramReportId } = useParams<{ reportId: string }>();
  const reportId = reportIdOverride ?? paramReportId;
  const { selectedAccountId } = useContext(AccountPickerContext);
  const hasAccess = useHasAccess();
  const [report, setReport] = useState<ReportType | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Keep a stable ref so the snapshot callback always uses the latest access
  // check without needing to re-register the Firestore listener.
  const hasAccessRef = useRef(hasAccess);
  hasAccessRef.current = hasAccess;

  useEffect(() => {
    if (!selectedAccountId || !reportId) return;
    let isActive = true;

    const unsubscribe = STORE.getReportsRef({ accountId: selectedAccountId })
      .doc(reportId)
      .onSnapshot((doc) => {
        if (!isActive) return;
        if (!doc.exists) {
          setIsLoading(false);
          return;
        }
        const r = doc.data() as ReportType;
        if (
          hasAccessRef.current({
            access: r.access,
            resource: r,
            type: 'report',
            typeId: r.id,
          })
        ) {
          setReport(r);
        }
        setIsLoading(false);
      });

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [selectedAccountId, reportId]);

  return { report, setReport, isLoading };
};

export default useReport;
